import { isRejectedWithValue, Middleware } from '@reduxjs/toolkit';
import { tokenRefreshService } from './utils/tokenRefreshService';
import { AUTH_FLOW_ENDPOINTS } from './constants/auth';

let isRefreshing = false;
let isLoggingOut = false; // ← ADD THIS FLAG
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

/**
 * Middleware to handle 401 errors and token refresh
 * Only refreshes token once even if multiple requests fail simultaneously
 */
export const tokenMiddleware: Middleware =
  (storeApi) => (next) => async (action) => {
    // Check if this is a rejected API call with 401
    // Sign-in flows answer a wrong password/code with 401; that is not an expired session.
    if (
      isRejectedWithValue(action) &&
      action.payload?.status === 401 &&
      !AUTH_FLOW_ENDPOINTS.has((action.meta as any)?.arg?.endpointName)
    ) {
      // console.log('[TokenMiddleware] 401 error detected');

      // If already logging out, don't process more 401s
      if (isLoggingOut) {
        // console.log('[TokenMiddleware] Logout in progress, ignoring 401');
        return next(action);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        // console.log('[TokenMiddleware] Starting token refresh...');

        try {
          const newToken = await tokenRefreshService.refresh();
          isRefreshing = false;
          // console.log(
          //   '[TokenMiddleware] Token refresh successful, retrying queued requests'
          // );

          // Notify all queued requests
          onTokenRefreshed(newToken);

          // Retry the original request that triggered the 401
          return storeApi.dispatch(action.meta.arg.originalArgs);
        } catch (error: any) {
          isRefreshing = false;
          isLoggingOut = true; // ← SET FLAG BEFORE LOGOUT
          // console.error('[TokenMiddleware] Token refresh failed:', error);

          // Clear queue
          refreshSubscribers = [];

          // Handle different error types
          if (error.status === 400) {
            // Manually expired/revoked token
            tokenRefreshService.logout(true, 'لطفاً دوباره وارد شوید.');
          } else {
            // Other errors (network, server down, etc.)
            tokenRefreshService.logout(true, 'لطفاً دوباره وارد شوید.');
          }

          return next(action);
        }
      } else {
        // Already refreshing - queue this request
        // console.log(
        //   '[TokenMiddleware] Queueing request until refresh completes'
        // );
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => {
            // console.log('[TokenMiddleware] Retrying queued request');
            resolve(storeApi.dispatch(action.meta.arg.originalArgs));
          });
        });
      }
    }

    return next(action);
  };
